/**
 * WebSocket Service
 * Manages WebSocket connections and message handling for the chat application
 */

import { logger } from '../utils/logger'; // Import a custom logger utility

// Get the WebSocket URL from environment variables or use a default
const WS_BASE_URL = process.env.REACT_APP_WEBSOCKET_URL || '/ws';

// Force the use of the path on the current server (Nginx proxy) instead of direct connections
const FORCE_RELATIVE_PATH = true;

/**
 * WebSocket connection manager class
 * Handles connection, message sending/receiving, and reconnection logic
 */
class WebSocketService {
  constructor() {
    this.socket = null;
    this.userId = null;
    this.messageHandlers = [];
    this.connectionStatusHandlers = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.lastReconnectTime = 0;
    this.minReconnectInterval = 5000; // Minimum 5 seconds between reconnect attempts
    this.isReconnecting = false;
    this.isConnecting = false; // Track active connection attempts
    this.socketUrl = null; // Store the active connection URL
  }

  /**
   * Connect to WebSocket server
   * @param {string} userId - User identifier for the connection
   * @param {string} token - Optional JWT authentication token
   */
  connect(userId, token = null) {
    // First check if user is authenticated - token is required
    if (!token) {
      logger.log('Not connecting to WebSocket: No authentication token provided');
      return;
    }
    
    // Don't connect if userId is null (unauthenticated user)
    if (!userId) {
      logger.log('Not connecting to WebSocket: No user ID provided (user is unauthenticated)');
      return;
    }
    
    // Check if we already have an active connection for this user
    if (this.socket && this.socket.readyState === WebSocket.OPEN && this.userId === userId) {
      logger.log(`Already connected to WebSocket for user ${userId}`);
      return;
    }
    
    this.userId = userId;
    this.closeConnection(); // Close any existing connection

    // Set a flag to track connection attempt
    if (this.isConnecting) {
      logger.log('Connection attempt already in progress, skipping duplicate request');
      return;
    }
    
    this.isConnecting = true;

    // Construct WebSocket URL with the base URL and user ID
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    
    // Construct WebSocket URL:
    // In Docker/Nginx setup, we always want to use relative paths through Nginx
    let wsUrl;
    
    // Always use the relative path through the current host to go through Nginx
    wsUrl = `${protocol}//${host}/ws/${userId}`;
    
    logger.log(`Using WebSocket URL: ${wsUrl}`);
    
    // Add token if available from parameters or localStorage
    const authToken = token || localStorage.getItem('token');
    const wsUrlWithToken = authToken ? `${wsUrl}?token=${authToken}` : wsUrl;

    logger.log(`Connecting to WebSocket at ${wsUrlWithToken}`);
    
    // Store the connection URL to avoid duplicate connections
    this.socketUrl = wsUrlWithToken;
    
    try {
      this.socket = new WebSocket(wsUrlWithToken);

      this.socket.onopen = () => {
        logger.log(`WebSocket connection established to ${wsUrlWithToken}`);
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this._notifyConnectionStatus(true);
      };

      this.socket.onmessage = (event) => {
        try {
          logger.log('WebSocket message received:', event.data);
          const data = JSON.parse(event.data);
          this._handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = (event) => {
        logger.log(`WebSocket connection closed. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
        logger.log(`Was clean close: ${event.wasClean}`);
        this.isConnecting = false;
        this._notifyConnectionStatus(false);
        
        // Don't attempt to reconnect if it was a clean close with certain codes
        if (event.wasClean && [1000, 1001].includes(event.code)) {
          logger.log('Clean close, not reconnecting immediately');
          // Reset reconnect attempts since this was a clean close
          this.reconnectAttempts = 0;
        } else {
          this._attemptReconnect();
        }
      };

      this.socket.onerror = (error) => {
        console.error(`WebSocket error for ${wsUrlWithToken}:`, error);
        this.isConnecting = false;
      };
    } catch (error) {
      console.error(`Error creating WebSocket connection to ${wsUrlWithToken}:`, error);
      this.isConnecting = false;
      this._attemptReconnect();
    }
  }

  /**
   * Close the WebSocket connection
   */
  closeConnection() {
    if (this.socket) {
      logger.log(`Closing existing WebSocket connection: ${this.socketUrl}`);
      
      try {
        // Use a clean close if possible
        this.socket.close(1000, "Normal closure");
      } catch (error) {
        console.error('Error closing WebSocket:', error);
      }
      
      this.socket = null;
      this.socketUrl = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Reset connection tracking flags but not reconnection attempts
    // This helps prevent racing between a close and new connection
    this.isConnecting = false;
  }

  /**
   * Send a message through the WebSocket connection
   * @param {Object|string} message - Message to send
   * @returns {boolean} Whether the message was sent successfully
   */
  sendMessage(message) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot send message: WebSocket is not connected');
      return false;
    }

    try {
      const messageData = typeof message === 'string' ? message : JSON.stringify(message);
      this.socket.send(messageData);
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      return false;
    }
  }

  /**
   * Register a handler for incoming messages
   * @param {Function} handler - Function to handle incoming messages
   * @returns {Function} Function to remove the handler
   */
  onMessage(handler) {
    if (typeof handler === 'function' && !this.messageHandlers.includes(handler)) {
      this.messageHandlers.push(handler);
    }
    return () => this.removeMessageHandler(handler);
  }

  /**
   * Remove a message handler
   * @param {Function} handler - Handler to remove
   */
  removeMessageHandler(handler) {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
  }

  /**
   * Register a handler for connection status changes
   * @param {Function} handler - Function to handle connection status changes
   * @returns {Function} Function to remove the handler
   */
  onConnectionStatus(handler) {
    if (typeof handler === 'function' && !this.connectionStatusHandlers.includes(handler)) {
      this.connectionStatusHandlers.push(handler);
    }
    return () => this.removeConnectionStatusHandler(handler);
  }

  /**
   * Remove a connection status handler
   * @param {Function} handler - Handler to remove
   */
  removeConnectionStatusHandler(handler) {
    this.connectionStatusHandlers = this.connectionStatusHandlers.filter(h => h !== handler);
  }

  /**
   * Handle incoming messages and distribute to registered handlers
   * @private
   * @param {Object} data - Message data
   */
  _handleMessage(data) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  /**
   * Notify all registered handlers of connection status changes
   * @private
   * @param {boolean} isConnected - Current connection status
   */
  _notifyConnectionStatus(isConnected) {
    this.connectionStatusHandlers.forEach(handler => {
      try {
        handler(isConnected);
      } catch (error) {
        console.error('Error in connection status handler:', error);
      }
    });
  }

  /**
   * Attempt to reconnect to WebSocket server
   * @private
   */
  _attemptReconnect() {
    const now = Date.now();
    
    // Don't attempt to reconnect if there's no user ID (unauthenticated)
    if (!this.userId) {
      logger.log('Not attempting to reconnect: No user ID available (user is unauthenticated)');
      return;
    }
    
    // Prevent multiple simultaneous reconnection attempts
    if (this.isReconnecting || this.isConnecting) {
      logger.log('Already attempting to reconnect or connect, skipping duplicate attempt');
      return;
    }

    // Check if maximum reconnection attempts reached
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.log(`Maximum reconnection attempts (${this.maxReconnectAttempts}) reached`);
      return;
    }

    // Check if we're trying to reconnect too quickly
    if (now - this.lastReconnectTime < this.minReconnectInterval) {
      logger.log(`Reconnect attempt too soon, delaying until minimum interval (${this.minReconnectInterval}ms) has passed`);
      
      // Clear any existing timeout and set a new one
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
      }
      
      this.reconnectTimeout = setTimeout(() => {
        this._attemptReconnect();
      }, this.minReconnectInterval);
      
      return;
    }
    
    this.isReconnecting = true;
    this.reconnectAttempts++;
    this.lastReconnectTime = now;
    
    // Calculate delay with exponential backoff
    const delay = Math.min(1000 * (2 ** this.reconnectAttempts), 30000); // Exponential backoff with max of 30 seconds
    
    logger.log(`Attempting to reconnect in ${delay / 1000} seconds (attempt ${this.reconnectAttempts} of ${this.maxReconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.isReconnecting = false;
      if (this.userId) {
        this.connect(this.userId);
      }
    }, delay);
  }
}

// Export a singleton instance of the WebSocket service
export const webSocketService = new WebSocketService();

// Static utility methods
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};
